/**
 * WhatsApp Chat Widget - Mombee Store
 * Native Shopify theme section JS for contextual WhatsApp support.
 *
 * Architecture: Single IIFE with five internal modules:
 *   1. PageDetector     – Identifies the current storefront page type
 *   2. ContextCollector – Gathers page-specific data from Liquid-rendered data attributes
 *   3. MessageBuilder   – Composes a pre-filled WhatsApp message from context
 *   4. URLGenerator     – Constructs the wa.me API URL
 *   5. UIController     – Manages popup interactions, animations, and accessibility
 *
 * No external dependencies. No jQuery.
 */
(function whatsappWidget() {
  'use strict';

  // =========================================================================
  // Module 1: PageDetector
  // Determines the current page type from the Liquid-injected template name
  // stored in the widget element's data-page-type attribute, with URL pattern
  // fallback for edge cases.
  // =========================================================================

  /**
   * Known Shopify template name → page type mappings.
   * Template names come from Shopify's {{ template.name }} variable.
   */
  var TEMPLATE_MAP = {
    'product': 'product',
    'collection': 'collection',
    'cart': 'cart',
    'search': 'search',
    'index': 'homepage',
    'tracking': 'order-tracking'
  };

  /**
   * URL pattern fallbacks when the template name attribute is unavailable.
   * Each entry is a regex tested against window.location.pathname.
   */
  var URL_PATTERNS = [
    { pattern: /\/products\//, type: 'product' },
    { pattern: /\/collections\//, type: 'collection' },
    { pattern: /\/cart\b/, type: 'cart' },
    { pattern: /\/search/, type: 'search' },
    { pattern: /\/orders\/|\/tracking/, type: 'order-tracking' },
    { pattern: /^\/$/, type: 'homepage' }
  ];

  /**
   * Determines the current page type from Liquid-injected template data.
   * First checks the data-page-type attribute (preferred, set by Liquid).
   * Falls back to URL pattern matching if attribute is missing or empty.
   *
   * @param {object} config - Config object containing widgetEl reference
   * @returns {'product'|'collection'|'cart'|'search'|'order-tracking'|'homepage'|'default'}
   */
  function detectPageType(config) {
    // Read the template name from the widget element's data attribute
    var widgetEl = config && config.widgetEl;
    var templateName = '';

    if (widgetEl) {
      templateName = (widgetEl.getAttribute('data-page-type') || '').trim().toLowerCase();
    }

    // Check against known template name mappings
    if (templateName && TEMPLATE_MAP.hasOwnProperty(templateName)) {
      return TEMPLATE_MAP[templateName];
    }

    // Fallback: inspect URL patterns for page type detection
    var pathname = window.location.pathname;
    for (var i = 0; i < URL_PATTERNS.length; i++) {
      if (URL_PATTERNS[i].pattern.test(pathname)) {
        return URL_PATTERNS[i].type;
      }
    }

    // Unknown page type — use default greeting
    return 'default';
  }

  // =========================================================================
  // Module 2: ContextCollector
  // Collects page-specific context from data attributes rendered by Liquid.
  // Implemented in task 2.2.
  // =========================================================================

  /**
   * Collects page-specific context from Liquid-rendered data attributes.
   * Each page type has its own set of relevant attributes; unrelated attributes
   * are ignored. Returns an object whose shape varies by page type.
   *
   * @param {string} pageType - Output of detectPageType()
   * @param {HTMLElement} widgetEl - The section root element containing data attributes
   * @returns {object} Context object with page-specific fields
   */
  function collectContext(pageType, widgetEl) {
    if (!widgetEl) {
      return {};
    }

    switch (pageType) {
      case 'product':
        return {
          title: widgetEl.getAttribute('data-product-title') || '',
          price: widgetEl.getAttribute('data-product-price') || '',
          compareAtPrice: widgetEl.getAttribute('data-product-compare-price') || '',
          variant: widgetEl.getAttribute('data-product-variant') || '',
          sku: widgetEl.getAttribute('data-product-sku') || '',
          url: widgetEl.getAttribute('data-product-url') || ''
        };

      case 'collection':
        return {
          name: widgetEl.getAttribute('data-collection-name') || '',
          url: widgetEl.getAttribute('data-collection-url') || ''
        };

      case 'cart':
        var itemsRaw = widgetEl.getAttribute('data-cart-items') || '[]';
        var items = [];
        try {
          items = JSON.parse(itemsRaw);
        } catch (e) {
          items = [];
        }
        return {
          items: items,
          total: widgetEl.getAttribute('data-cart-total') || '',
          url: widgetEl.getAttribute('data-cart-url') || ''
        };

      case 'search':
        return {
          query: widgetEl.getAttribute('data-search-query') || ''
        };

      case 'homepage':
      case 'order-tracking':
      default:
        return {};
    }
  }

  // =========================================================================
  // Module 3: MessageBuilder
  // Composes a human-readable, pre-filled WhatsApp message from page context.
  // Implemented in task 2.3.
  // =========================================================================

  /**
   * Composes a human-readable, pre-filled WhatsApp message from page context.
   * Each page type has its own message template. Empty/undefined fields are
   * skipped gracefully. The returned string is plain text (not URL-encoded);
   * encoding is handled by the URLGenerator module.
   *
   * @param {string} pageType - Output of detectPageType()
   * @param {object} context - Output of collectContext()
   * @param {object} config - Merchant settings (businessName, greeting)
   * @returns {string} Plain text message
   */
  function buildMessage(pageType, context, config) {
    var businessName = (config && config.businessName) || '';
    var greeting = (config && config.greeting) || '';

    switch (pageType) {
      case 'homepage':
        return greeting;

      case 'product':
        return buildProductMessage(context, businessName);

      case 'collection':
        return buildCollectionMessage(context, businessName);

      case 'cart':
        return buildCartMessage(context, businessName);

      case 'search':
        return buildSearchMessage(context, businessName);

      case 'order-tracking':
        return 'Hi ' + businessName + '! I need help tracking my order.';

      default:
        return greeting;
    }
  }

  /**
   * Builds the product page message. Includes compare-at price, variant, and
   * SKU lines only when those fields are non-empty.
   */
  function buildProductMessage(context, businessName) {
    var lines = [];
    lines.push('Hi ' + businessName + '! I\'m interested in:');
    lines.push('');

    if (context.title) {
      lines.push('\u{1F4E6} ' + context.title);
    }
    if (context.price) {
      lines.push('\u{1F4B0} Price: ' + context.price);
    }
    if (context.compareAtPrice) {
      lines.push('\u{1F3F7}\uFE0F Was: ' + context.compareAtPrice);
    }
    if (context.variant) {
      lines.push('\u{1F50D} Variant: ' + context.variant);
    }
    if (context.sku) {
      lines.push('\u{1F4CB} SKU: ' + context.sku);
    }
    if (context.url) {
      lines.push('\u{1F517} ' + context.url);
    }

    lines.push('');
    lines.push('Could you help me with this product?');

    return lines.join('\n');
  }

  /**
   * Builds the collection page message.
   */
  function buildCollectionMessage(context, businessName) {
    var lines = [];
    lines.push('Hi ' + businessName + '! I\'m browsing your ' + (context.name || '') + ' collection.');

    if (context.url) {
      lines.push('\u{1F517} ' + context.url);
    }

    lines.push('');
    lines.push('Can you recommend something?');

    return lines.join('\n');
  }

  /**
   * Builds the cart page message. Lists each item as "- {name} x{quantity}".
   * Falls back to a helpful message when the cart is empty.
   */
  function buildCartMessage(context, businessName) {
    var items = context.items || [];

    // Empty cart fallback
    if (items.length === 0) {
      return 'Hi ' + businessName + '! My cart is empty, can you help me find products?';
    }

    var lines = [];
    lines.push('Hi ' + businessName + '! I have a question about my cart:');
    lines.push('');

    for (var i = 0; i < items.length; i++) {
      var item = items[i];
      var name = (item && item.name) || '';
      var quantity = (item && item.quantity) || 1;
      lines.push('- ' + name + ' x' + quantity);
    }

    if (context.total) {
      lines.push('\u{1F4B0} Total: ' + context.total);
    }

    lines.push('');
    lines.push('Can you help?');

    return lines.join('\n');
  }

  /**
   * Builds the search page message.
   */
  function buildSearchMessage(context, businessName) {
    var query = (context && context.query) || '';
    return 'Hi ' + businessName + '! I searched for "' + query + '" on your store. Can you help me find what I\'m looking for?';
  }

  // =========================================================================
  // Module 4: URLGenerator
  // Constructs the final wa.me URL with phone number and encoded message.
  // Implemented in task 2.4.
  // =========================================================================

  /**
   * Constructs the final wa.me URL with sanitized phone number and encoded message.
   * Strips all non-digit characters from the phone (wa.me expects digits only in the path).
   * URL-encodes the message using encodeURIComponent for safe transport.
   *
   * @param {string} phone - WhatsApp number with country code (may contain +, spaces, dashes, parens)
   * @param {string} message - Plain text message to pre-fill
   * @returns {string} Full https://wa.me/{phone}?text={encodedMessage} URL
   */
  function generateURL(phone, message) {
    // Strip everything except digits (wa.me expects only digits in the URL path)
    var sanitizedPhone = (phone || '').replace(/\D/g, '');

    // URL-encode the message for safe inclusion in query parameter
    var encodedMessage = encodeURIComponent(message || '');

    return 'https://wa.me/' + sanitizedPhone + '?text=' + encodedMessage;
  }

  // =========================================================================
  // Module 5: UIController
  // Manages popup open/close, animations, focus trap, keyboard handling,
  // and aria-expanded state management.
  // Implemented in task 2.5.
  // =========================================================================

  /**
   * Selector for all focusable elements used in focus trapping within the popup.
   */
  var FOCUSABLE_SELECTOR = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

  /**
   * Initializes the UI controller for the WhatsApp chat widget.
   * Sets up event listeners for open/close interactions, focus trapping,
   * keyboard handling, aria-expanded state, and the CTA message pipeline.
   *
   * @param {HTMLElement} widgetEl - Section root element
   * @param {object} config - Merchant settings (phone, businessName, greeting, etc.)
   */
  function initUI(widgetEl, config) {
    var button = widgetEl.querySelector('.whatsapp-widget-button');
    var popup = widgetEl.querySelector('.whatsapp-widget-popup');
    var closeBtn = widgetEl.querySelector('.whatsapp-widget-close');
    var ctaBtn = widgetEl.querySelector('.whatsapp-widget-cta');

    // Guard: all required elements must exist
    if (!button || !popup) {
      return;
    }

    // Track whether the popup is currently open
    var isOpen = false;

    /**
     * Opens the popup panel with animation class and updates accessibility state.
     */
    function openPopup() {
      if (isOpen) return;
      isOpen = true;
      popup.classList.add('whatsapp-widget-popup--open');
      button.setAttribute('aria-expanded', 'true');

      // Move focus to the first focusable element inside the popup
      var focusables = popup.querySelectorAll(FOCUSABLE_SELECTOR);
      if (focusables.length > 0) {
        focusables[0].focus();
      }
    }

    /**
     * Closes the popup panel with animation class and returns focus to the button.
     */
    function closePopup() {
      if (!isOpen) return;
      isOpen = false;
      popup.classList.remove('whatsapp-widget-popup--open');
      button.setAttribute('aria-expanded', 'false');

      // Return focus to the floating button
      button.focus();
    }

    /**
     * Toggles the popup open/closed state.
     */
    function togglePopup() {
      if (isOpen) {
        closePopup();
      } else {
        openPopup();
      }
    }

    // --- Event: Floating button click toggles popup ---
    button.addEventListener('click', function (e) {
      e.preventDefault();
      togglePopup();
    });

    // --- Event: Close button click closes popup ---
    if (closeBtn) {
      closeBtn.addEventListener('click', function (e) {
        e.preventDefault();
        closePopup();
      });
    }

    // --- Event: Escape key closes popup ---
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && isOpen) {
        closePopup();
      }
    });

    // --- Event: Outside click closes popup ---
    document.addEventListener('click', function (e) {
      if (!isOpen) return;
      // If the click target is outside the widget container, close the popup
      if (!widgetEl.contains(e.target)) {
        closePopup();
      }
    });

    // --- Focus trapping within the popup while open ---
    popup.addEventListener('keydown', function (e) {
      if (e.key !== 'Tab' || !isOpen) return;

      var focusables = popup.querySelectorAll(FOCUSABLE_SELECTOR);
      if (focusables.length === 0) return;

      var firstFocusable = focusables[0];
      var lastFocusable = focusables[focusables.length - 1];

      if (e.shiftKey) {
        // Shift+Tab: if focus is on first element, wrap to last
        if (document.activeElement === firstFocusable) {
          e.preventDefault();
          lastFocusable.focus();
        }
      } else {
        // Tab: if focus is on last element, wrap to first
        if (document.activeElement === lastFocusable) {
          e.preventDefault();
          firstFocusable.focus();
        }
      }
    });

    // --- Event: CTA button click runs the full message pipeline ---
    if (ctaBtn) {
      ctaBtn.addEventListener('click', function (e) {
        e.preventDefault();
        try {
          var pageType = detectPageType(config);
          var context = collectContext(pageType, widgetEl);
          var message = buildMessage(pageType, context, config);
          var url = generateURL(config.phone, message);
          window.open(url, '_blank');
        } catch (err) {
          console.error('[whatsapp-widget] CTA pipeline error:', err);
        }
      });
    }
  }

  // =========================================================================
  // Initialization
  // Reads config from the widget element's data attribute and boots the widget
  // after DOM content has loaded.
  // =========================================================================

  document.addEventListener('DOMContentLoaded', function whatsappWidgetInit() {
    try {
      // Find the widget element in the DOM
      var widgetEl = document.querySelector('[data-whatsapp-config]');
      if (!widgetEl) {
        return; // Widget not present on this page (phone number not configured)
      }

      // Parse merchant configuration from the data attribute
      var configRaw = widgetEl.getAttribute('data-whatsapp-config');
      var config = JSON.parse(configRaw);
      config.widgetEl = widgetEl;

      // Detect the current page type
      var pageType = detectPageType(config);
      config.pageType = pageType;

      // Initialize UI controller
      initUI(widgetEl, config);
    } catch (err) {
      // Fail silently — widget should never crash the storefront
      console.error('[whatsapp-widget] Initialization error:', err);
    }
  });

})();
